<template>
  <Teleport to="body">
    <Transition name="popup-fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-[9000] flex justify-center backdrop-blur-sm p-4 sm:p-5"
        :class="[overlayClass, alignClass]"
        @click.self="handleOverlayClick"
      >
        <div
          class="bg-white rounded-2xl shadow-2xl relative w-full"
          :class="[maxWidthClass, scrollableClass]"
          role="dialog"
          aria-modal="true"
        >
          <button
            v-if="showCloseButton"
            class="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 z-10 transition"
            type="button"
            @click="handleClose"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onBeforeUnmount } from 'vue';

// Module-level counter so nested modals don't unlock scroll prematurely
let _scrollLockCount = 0;

function lockScroll() {
  if (_scrollLockCount === 0) {
    document.body.classList.add('overflow-hidden');
  }
  _scrollLockCount++;
}

function unlockScroll() {
  if (_scrollLockCount > 0) _scrollLockCount--;
  if (_scrollLockCount === 0) {
    document.body.classList.remove('overflow-hidden');
  }
}

const props = withDefaults(defineProps<{
  modelValue: boolean;
  size?: 'sm' | 'md' | 'lg';
  align?: 'center' | 'bottom';
  closeOnOverlay?: boolean;
  showCloseButton?: boolean;
  scrollable?: boolean;
  dark?: boolean;
}>(), {
  size: 'md',
  align: 'center',
  closeOnOverlay: true,
  showCloseButton: false,
  scrollable: false,
  dark: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
}>();

const overlayClass = computed(() =>
  props.dark ? 'bg-black/60' : 'bg-black/30'
);

const alignClass = computed(() =>
  props.align === 'bottom' ? 'items-end sm:items-center' : 'items-center'
);

const maxWidthClass = computed(() =>
  ({ sm: 'max-w-[380px]', md: 'max-w-[520px]', lg: 'max-w-xl' })[props.size]
);

const scrollableClass = computed(() =>
  props.scrollable ? 'max-h-[92vh] overflow-y-auto' : 'overflow-hidden'
);

function handleClose() {
  emit('update:modelValue', false);
  emit('close');
}

function handleOverlayClick() {
  if (props.closeOnOverlay) handleClose();
}

function handleEsc(event: KeyboardEvent) {
  if (event.key === 'Escape') handleClose();
}

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      lockScroll();
      document.addEventListener('keydown', handleEsc);
    } else {
      unlockScroll();
      document.removeEventListener('keydown', handleEsc);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (props.modelValue) unlockScroll();
  document.removeEventListener('keydown', handleEsc);
});
</script>

<style scoped>
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.2s ease;
}
.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}
</style>
